import { AnalyticsConsentSchema, type AnalyticsConsent } from '@lineage/core';
import { create } from 'zustand';
import type { PostHog } from 'posthog-js';

/**
 * Product analytics with PostHog (PRD 13, docs/ANALYTICS.md).
 *
 * - Where the law needs opt-in (EU, EEA, UK, Switzerland, or an unknown country) nothing loads until the person
 *   taps Allow on the banner. Everywhere else it runs with notice in the Privacy Policy and an opt-out in Settings.
 * - The choice lives on the device (so it works signed out) and on the account (so it follows the person); the
 *   account wins when /me loads, like the language preferences.
 * - Signed in, events are linked to the account's internal id (a random ULID), never the email, name or Firebase uid.
 * - No session recording, no autocapture (it would read names off the screen), no surveys, no external scripts.
 *   Invite tokens and tree ids are cut out of every URL before it leaves the device.
 */
const KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? 'https://us.i.posthog.com';
const ORIGIN = (import.meta.env.VITE_API_ORIGIN as string | undefined) ?? '';
const STORE = 'lineage.analyticsConsent';

const read = (): AnalyticsConsent | null => {
  try {
    const v = AnalyticsConsentSchema.safeParse(localStorage.getItem(STORE));
    return v.success ? v.data : null;
  } catch {
    return null;
  }
};
const write = (v: AnalyticsConsent | null) => {
  try {
    if (v) localStorage.setItem(STORE, v);
    else localStorage.removeItem(STORE);
  } catch {
    /* private mode or prerender */
  }
};

interface ConsentState {
  /** This device's choice; null = not asked. */
  consent: AnalyticsConsent | null;
  /** Country from /api/geo; undefined until it answers. */
  country: string | null | undefined;
  /** Whether that country needs the opt-in banner; undefined until known. */
  required: boolean | undefined;
}

export const useConsent = create<ConsentState>(() => ({ consent: read(), country: undefined, required: undefined }));

/** Whether analytics is on for this device right now (Settings switch). */
export const useAnalyticsOn = () =>
  useConsent((s) => Boolean(KEY) && (s.consent ? s.consent === 'granted' : s.required === false));

/** Show the banner only where the law needs it, and only until the person chooses. */
export const useShowBanner = () => useConsent((s) => Boolean(KEY) && s.required === true && s.consent === null);

/** The header the API uses for this device's choice until the account has one (apps/api/src/lib/events.ts). */
export const consentHeader = (): Record<string, string> => {
  const c = useConsent.getState().consent;
  return c ? { 'X-Analytics-Consent': c } : {};
};

/** Invite tokens and tree ids out of a URL or path; everything else kept. Exported for tests. */
export function scrubUrl(url: string): string {
  return url.replace(/\/i\/[^/?#]+/g, '/i/:token').replace(/\/t\/[^/?#]+/g, '/t/:id').replace(/[?#].*$/, '');
}

const URL_KEY = /url|referr|pathname|href/i;

let ph: PostHog | null = null;
let loading: Promise<PostHog | null> | null = null;
let userId: string | null = null;

/** An explicit choice wins; otherwise on only where no banner is needed. Off until the country is known. */
const allowed = () => {
  const s = useConsent.getState();
  if (!KEY || s.required === undefined) return false;
  return s.consent ? s.consent === 'granted' : !s.required;
};

async function load(): Promise<PostHog | null> {
  if (ph) return ph;
  loading ??= import('posthog-js/no-external').then(({ default: posthog }) => {
    posthog.init(KEY!, {
      api_host: HOST,
      persistence: 'localStorage',
      person_profiles: 'identified_only',
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      disable_session_recording: true,
      disable_surveys: true,
      capture_dead_clicks: false,
      capture_heatmaps: false,
      capture_exceptions: false,
      capture_performance: false,
      advanced_disable_flags: true,
      disable_external_dependency_loading: true,
      // Every URL-like property (current URL, path, referrer, session entry URL and their $set copies) is scrubbed.
      before_send: (event) => {
        if (!event) return event;
        for (const bag of [event.properties, event.$set, event.$set_once]) {
          if (!bag) continue;
          for (const k of Object.keys(bag)) if (URL_KEY.test(k) && typeof bag[k] === 'string') bag[k] = scrubUrl(bag[k] as string);
        }
        return event;
      },
    });
    const s = useConsent.getState();
    posthog.register({ platform: platform(), country: s.country ?? undefined });
    ph = posthog;
    return posthog;
  });
  return loading;
}

const platform = (): string => {
  const cap = (globalThis as { Capacitor?: { getPlatform?: () => string } }).Capacitor;
  return cap?.getPlatform?.() ?? 'web';
};

/** Starts or stops PostHog to match the current choice. */
async function apply() {
  if (allowed()) {
    const first = !ph;
    const p = await load();
    if (!p) return;
    if (p.has_opted_out_capturing()) p.opt_in_capturing();
    if (userId) p.identify(userId);
    if (first) trackPage(window.location.pathname);
  } else if (ph) {
    ph.opt_out_capturing();
  }
}

/** Asks the API where the visitor is, once per launch. Offline: analytics stays off for now. */
export async function initAnalytics() {
  if (!KEY || typeof window === 'undefined') return;
  try {
    const res = await fetch(`${ORIGIN}/api/geo`);
    const geo = (await res.json()) as { country: string | null; consentRequired: boolean };
    useConsent.setState({ country: geo.country, required: geo.consentRequired });
  } catch {
    return;
  }
  await apply();
}

/** The person chose on the banner or in Settings. */
export function setConsent(v: AnalyticsConsent) {
  write(v);
  useConsent.setState({ consent: v });
  void apply();
}

/** Copies the account's choice onto this device (the account wins). Returns the device choice the account lacks. */
export function syncConsent(account: AnalyticsConsent | null | undefined): AnalyticsConsent | null {
  if (account === undefined) return null;
  const device = useConsent.getState().consent;
  if (account) {
    if (account !== device) setConsent(account);
    return null;
  }
  return device;
}

/** Links later events to the account (internal id only), or forgets it on sign-out. */
export function identify(id: string | null) {
  if (id === userId) return;
  const was = userId;
  userId = id;
  if (!ph || !allowed()) return;
  if (id) ph.identify(id);
  else if (was) ph.reset();
}

/** A page view with the path scrubbed of tokens and ids. */
export function trackPage(path: string) {
  if (!ph || !allowed()) return;
  ph.capture('$pageview', { $current_url: scrubUrl(window.location.origin + path) });
}

/** A screen event that the server can't see (e.g. which button on the landing page). No names or free text. */
export function trackClient(event: string, props: Record<string, string | number | boolean> = {}) {
  if (!ph || !allowed()) return;
  ph.capture(event, props);
}
